/**
 * Hook (M2): rope from a chosen anchor to the ragdoll's nearer hand.
 * Anchor selection: nearest valid anchor within hookRadius, with line-of-sight
 * check against platforms (sampled, see hasLineOfSight below).
 */

import Matter from 'matter-js';
import { Graphics } from 'pixi.js';
import type { Anchor } from './entities/Anchor.js';
import { Ragdoll } from './Ragdoll.js';
import { dist2 } from '../util/math.js';

export interface HookEvents {
  onAttach?: () => void;
  onRelease?: () => void;
  onMiss?: () => void;
}

export class Hook {
  private constraint: Matter.Constraint | null = null;
  private attachedAnchor: Anchor | null = null;
  private attachedHand: Matter.Body | null = null;
  private lastHand: 'L' | 'R' | null = null;
  private initialLength = 0;
  /** Pump: shorten the rope while attached. Converts player position into
   * kinetic energy on each swing, matching Stickman Hook's "always swing
   * faster than you arrived" feel. */
  private static readonly PUMP_RATE_PER_SEC = 90; // px/s shortening — strong pump
  private static readonly MIN_LENGTH_FRAC = 0.35; // can pull in to 35% of original
  readonly view: Graphics;

  constructor(
    private readonly engine: Matter.Engine,
    private readonly ragdoll: Ragdoll,
    private readonly platformBodies: Matter.Body[],
    private readonly events: HookEvents = {},
  ) {
    this.view = new Graphics();
  }

  tryAttach(anchors: readonly Anchor[]): boolean {
    if (this.constraint) return false;

    const px = this.ragdoll.position.x;
    const py = this.ragdoll.position.y;

    let best: Anchor | null = null;
    let bestD2 = Infinity;
    for (const a of anchors) {
      const d2 = dist2(px, py, a.x, a.y);
      if (d2 > a.hookRadius * a.hookRadius) continue;
      if (d2 >= bestD2) continue;
      if (!this.hasLineOfSight(px, py, a.x, a.y)) continue;
      best = a;
      bestD2 = d2;
    }
    if (!best) {
      this.events.onMiss?.();
      return false;
    }

    const hand = this.pickAlternatingHand(best.x, best.y);
    const offset = Ragdoll.handAttachOffset();

    const cos = Math.cos(hand.angle);
    const sin = Math.sin(hand.angle);
    const handWorldX = hand.position.x + offset.x * cos - offset.y * sin;
    const handWorldY = hand.position.y + offset.x * sin + offset.y * cos;
    const length = Math.hypot(handWorldX - best.x, handWorldY - best.y);

    this.constraint = Matter.Constraint.create({
      pointA: { x: best.x, y: best.y },
      bodyB: hand,
      pointB: offset,
      length,
      // Slightly springy + damped — feels like a flexible rope rather than a
      // rigid stick. Stiffness < 1 lets it stretch/recoil a touch on impacts.
      stiffness: 0.85,
      damping: 0.04,
    });
    Matter.Composite.add(this.engine.world, this.constraint);
    this.attachedAnchor = best;
    this.attachedHand = hand;
    this.lastHand = hand === this.ragdoll.leftHand ? 'L' : 'R';
    this.initialLength = length;
    best.setHighlighted(true);
    this.events.onAttach?.();
    return true;
  }

  detach(): void {
    if (!this.constraint) return;
    Matter.Composite.remove(this.engine.world, this.constraint);
    this.constraint = null;
    this.attachedHand = null;
    this.initialLength = 0;
    if (this.attachedAnchor) {
      this.attachedAnchor.setHighlighted(false);
      this.attachedAnchor = null;
    }
    this.events.onRelease?.();
  }

  /** Per-frame: shorten the rope while attached (pendulum pump). Also keeps
   *  the constraint's anchor point glued to a moving anchor's live position
   *  so the player swings with anchors that follow paths. */
  step(dtSec: number): void {
    if (!this.constraint || this.initialLength <= 0) return;
    if (this.attachedAnchor && this.constraint.pointA) {
      this.constraint.pointA.x = this.attachedAnchor.x;
      this.constraint.pointA.y = this.attachedAnchor.y;
    }
    const minLen = this.initialLength * Hook.MIN_LENGTH_FRAC;
    const next = this.constraint.length - Hook.PUMP_RATE_PER_SEC * dtSec;
    this.constraint.length = next < minLen ? minLen : next;
  }

  render(): void {
    this.view.clear();
    if (!this.constraint || !this.attachedAnchor || !this.attachedHand) return;
    const a = this.attachedAnchor;
    const hand = this.attachedHand;
    const offset = Ragdoll.handAttachOffset();
    // Prefer the ragdoll's interpolated render-space transform so the rope
    // tip stays glued to the rendered hand instead of the raw physics body
    // (which is one fixed step behind on high-refresh displays).
    const disp = this.ragdoll.displayTransform(hand);
    const cos = disp ? disp.cos : Math.cos(hand.angle);
    const sin = disp ? disp.sin : Math.sin(hand.angle);
    const baseX = disp ? disp.x : hand.position.x;
    const baseY = disp ? disp.y : hand.position.y;
    const hx = baseX + offset.x * cos - offset.y * sin;
    const hy = baseY + offset.x * sin + offset.y * cos;

    // Draw the rope as a series of small segments along a quadratic bezier
    // sagging downward at the midpoint. Sag scales with how slack the rope
    // is (current chord length vs configured constraint length) plus a small
    // baseline so it never looks perfectly straight.
    const dx = hx - a.x;
    const dy = hy - a.y;
    const chord = Math.hypot(dx, dy);
    const restLen = this.constraint.length;
    const slack = Math.max(0, restLen - chord); // 0 when taut
    const sag = Math.min(restLen * 0.18, 6 + slack * 0.6);
    const mx = (a.x + hx) / 2;
    const my = (a.y + hy) / 2 + sag;

    const SEG = 14;
    this.view.moveTo(a.x, a.y);
    for (let i = 1; i <= SEG; i++) {
      const t = i / SEG;
      const omt = 1 - t;
      const x = omt * omt * a.x + 2 * omt * t * mx + t * t * hx;
      const y = omt * omt * a.y + 2 * omt * t * my + t * t * hy;
      this.view.lineTo(x, y);
    }
    this.view.stroke({ color: 0xffffff, width: 2, alpha: 0.95 });
  }

  get isAttached(): boolean {
    return this.constraint !== null;
  }

  /** Prefer the hand opposite to whichever launched last. Falls back to the
   * closer hand if the alternating choice would be wildly far away. */
  private pickAlternatingHand(targetX: number, targetY: number): Matter.Body {
    const closer = this.ragdoll.pickHookHand(targetX, targetY);
    if (this.lastHand === null) return closer;
    const wantLeft = this.lastHand === 'R';
    const preferred = wantLeft ? this.ragdoll.leftHand : this.ragdoll.rightHand;
    // If preferred is more than ~2x further than closer, just use closer
    // (avoids cartoonish back-bend reaches across the body).
    const dPref2 = (preferred.position.x - targetX) ** 2 + (preferred.position.y - targetY) ** 2;
    const dClose2 = (closer.position.x - targetX) ** 2 + (closer.position.y - targetY) ** 2;
    return dPref2 > dClose2 * 4 ? closer : preferred;
  }

  /** Sampled line-of-sight: rejects if any sample falls inside any solid
   *  obstacle. Bodies whose `isSensor` is true (e.g. an open gate) are
   *  skipped so they don't block the rope. */
  private hasLineOfSight(x0: number, y0: number, x1: number, y1: number): boolean {
    if (this.platformBodies.length === 0) return true;
    const samples = 8;
    for (let i = 1; i < samples; i++) {
      const t = i / samples;
      const x = x0 + (x1 - x0) * t;
      const y = y0 + (y1 - y0) * t;
      for (const body of this.platformBodies) {
        if (body.isSensor) continue;
        if (!Matter.Bounds.contains(body.bounds, { x, y })) continue;
        if (Matter.Vertices.contains(body.vertices, { x, y })) return false;
      }
    }
    return true;
  }
}
